import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateChannelsList } from '@/entities/channel';
import { useInvalidateAllCompanyChannels } from '@/entities/company';
import { deleteChannelById } from '@/shared/api/channels';
import { useGetConfirmation } from '@/shared/ui/Confirmations/confirmation';
import { useNotifications } from '@/shared/ui/Notifications';

export function useDeleteChannel() {
  const { t } = useTranslation();

  const { addNotification } = useNotifications();

  const invalidateChannelsList = useInvalidateChannelsList();
  const invalidateAllCompanyChannels = useInvalidateAllCompanyChannels();
  const { getConfirmation, setPending, closeConfirmation } =
    useGetConfirmation();

  const deleteChannelMutation = useMutation({
    mutationFn: (channelId: string): Promise<void> =>
      deleteChannelById(channelId),
    onSuccess: async () => {
      await invalidateChannelsList();
      await invalidateAllCompanyChannels();
    },
    onSettled: () => {
      setPending(false);
      closeConfirmation();
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  const deleteChannel = async (
    channelId: string,
    onDeleted?: () => void
  ): Promise<void> => {
    const isConfirm = await getConfirmation({
      shouldCloseOnConfirm: false,
      title: t('channels:deleteChannel'),
      description: t('channels:deleteChannelConfirm'),
      confirmText: t('channels:deleteChannel'),
    });

    if (!isConfirm) return;

    setPending(true);
    return deleteChannelMutation.mutateAsync(channelId, {
      onSuccess: async () => {
        onDeleted?.();
      },
    });
  };

  return {
    deleteChannel,
    ...deleteChannelMutation,
  };
}
